import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  getWorkspaceTask,
  saveWorkspaceResult,
  resetWorkspace,
  resetSaveResultStatus,
  updateLocalDecisions,
  updateLocalChecklist,
  updateLocalComments,
} from "src/store/auditWorkspaceSlice";
import type {
  WorkspaceTask,
  WorkspaceResult,
  UseCaseConfig,
  FieldDecision,
  ChecklistResponseValue,
} from "../shared/types";

export interface UseAuditWorkspaceReturn {
  task: WorkspaceTask | null;
  config: UseCaseConfig | null;
  result: WorkspaceResult | null;
  allTasks: WorkspaceTask[];
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  saveSuccess: boolean;
  saveError: boolean;
  setDecisions: (decisions: Record<string, FieldDecision>) => void;
  setChecklist: (checklist: Record<string, ChecklistResponseValue>) => void;
  setComments: (comments: string) => void;
  saveDraft: () => void;
  submitResult: () => void;
  resetSave: () => void;
  reset: () => void;
}

export function useAuditWorkspace(taskId: string | undefined): UseAuditWorkspaceReturn {
  const dispatch = useAppDispatch();

  const { task, config, result, allTasks, getTaskLoading, saveResultLoading } = useAppSelector(
    (state) => state.auditWorkspaceSlice,
  );

  const { alias, userLoading } = useAppSelector((state) => state.user);

  // Fetch task data when taskId or alias changes.
  useEffect(() => {
    if (!taskId || !alias) return;
    if (getTaskLoading === "idle") {
      dispatch(getWorkspaceTask({ taskId, currentAlias: alias }));
    }
  }, [dispatch, taskId, alias, getTaskLoading]);

  // Reset on mount so stale data from a prior task does not flash.
  useEffect(() => {
    dispatch(resetWorkspace());
    if (saveResultLoading !== "idle") dispatch(resetSaveResultStatus());
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const aliasResolved = userLoading === "fulfilled" || userLoading === "rejected";
  const isLoading = !aliasResolved
    ? true
    : alias
      ? getTaskLoading === "idle" || getTaskLoading === "pending"
      : false;

  const isError = (aliasResolved && !alias) || getTaskLoading === "rejected";

  const isSaving = saveResultLoading === "pending";
  const saveSuccess = saveResultLoading === "fulfilled";
  const saveError = saveResultLoading === "rejected";

  const setDecisions = useCallback(
    (decisions: Record<string, FieldDecision>) => dispatch(updateLocalDecisions(decisions)),
    [dispatch],
  );

  const setChecklist = useCallback(
    (checklist: Record<string, ChecklistResponseValue>) =>
      dispatch(updateLocalChecklist(checklist)),
    [dispatch],
  );

  const setComments = useCallback(
    (comments: string) => dispatch(updateLocalComments(comments)),
    [dispatch],
  );

  const saveDraft = useCallback(() => {
    if (!result) return;
    dispatch(saveWorkspaceResult({ result, submit: false }));
  }, [dispatch, result]);

  const submitResult = useCallback(() => {
    if (!result) return;
    dispatch(saveWorkspaceResult({ result, submit: true }));
  }, [dispatch, result]);

  const resetSave = useCallback(() => {
    dispatch(resetSaveResultStatus());
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch(resetWorkspace());
  }, [dispatch]);

  return {
    task,
    config,
    result,
    allTasks,
    isLoading,
    isError,
    isSaving,
    saveSuccess,
    saveError,
    setDecisions,
    setChecklist,
    setComments,
    saveDraft,
    submitResult,
    resetSave,
    reset,
  };
}
